import React, { cloneElement, isValidElement, useState } from "react";
import GlobalOuterLayout from "components/routes/global-outer-layout/GlobalOuterLayout";
import GlobalHeader from "components/v2/global-header/GlobalHeader";
import SideBar from "components/v2/sidebar/SideBar";
import Footer from "components/footer/Footer";
import { NotificationProvider } from "contexts/NotificationContext";
import useDesktopChecker from "hooks/useDesktopChecker";
import useTabletChecker from "hooks/useTabletChecker";
import TabletNavOverlay from "components/v2/tablet-nav-overlay/TabletNavOverlay";
import "./globalNavigationShell.scss";

export type NavSlotPlacement = "sidebar" | "sidebar-bottom" | "none";

export interface GlobalNavigationShellProps {
  readonly notificationsEnabled: boolean;
  readonly children: React.ReactNode;
  readonly navSlot?: React.ReactNode;
  readonly navSlotPlacement?: NavSlotPlacement;
  readonly contentHeaderSlot?: React.ReactNode;
  /** Bottom navigation frame for mobile viewports (e.g. MobileBottomNav). */
  readonly mobileNavSlot?: React.ReactNode;
}

interface GlobalNavigationShellContentProps {
  readonly children: React.ReactNode;
  readonly navSlot?: React.ReactNode;
  readonly navSlotPlacement: NavSlotPlacement;
  readonly contentHeaderSlot?: React.ReactNode;
  readonly mobileNavSlot?: React.ReactNode;
}

const GlobalNavigationShellContent: React.FC<GlobalNavigationShellContentProps> = ({
  children,
  navSlot,
  navSlotPlacement,
  contentHeaderSlot,
  mobileNavSlot,
}) => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();

  const renderedNavSlot = isValidElement(navSlot)
    ? cloneElement(navSlot as React.ReactElement<Record<string, unknown>>, { isNavOpen, setIsNavOpen, isDesktop, isTablet })
    : navSlot;

  const showNavInSidebar = Boolean(navSlot) && navSlotPlacement !== "none";

  if (isDesktop) {
    return (
      <GlobalOuterLayout>
        <SideBar>
          {showNavInSidebar && (
            <div className={`global-navigation-shell__nav-slot global-navigation-shell__nav-slot--${navSlotPlacement}`}>
              {renderedNavSlot}
            </div>
          )}
        </SideBar>
        <div className="global-outer-layout__wrapper">
          <GlobalHeader isNavOpen={isNavOpen} setIsNavOpen={setIsNavOpen} />
          {contentHeaderSlot && <div className="global-navigation-shell__content-header">{contentHeaderSlot}</div>}
          <main className="global-navigation-shell__content">{children}</main>
          <Footer />
        </div>
      </GlobalOuterLayout>
    );
  }

  return (
    <GlobalOuterLayout>
      <div className="global-navigation-shell__mobile-container">
        <GlobalHeader isNavOpen={isNavOpen} setIsNavOpen={setIsNavOpen} />
        {contentHeaderSlot && <div className="global-navigation-shell__content-header">{contentHeaderSlot}</div>}
        <main className="global-navigation-shell__content">{children}</main>
        <Footer />
        {isTablet && showNavInSidebar && (
          <TabletNavOverlay isOpen={isNavOpen} onClose={() => setIsNavOpen(false)}>
            <div className={`global-navigation-shell__nav-slot global-navigation-shell__nav-slot--${navSlotPlacement}`}>
              {renderedNavSlot}
            </div>
          </TabletNavOverlay>
        )}
        {!isTablet && mobileNavSlot}
      </div>
    </GlobalOuterLayout>
  );
};

const GlobalNavigationShell: React.FC<GlobalNavigationShellProps> = ({
  notificationsEnabled,
  children,
  navSlot,
  navSlotPlacement = "sidebar",
  contentHeaderSlot,
  mobileNavSlot,
}) => {
  return (
    <NotificationProvider enabled={notificationsEnabled}>
      <GlobalNavigationShellContent
        navSlot={navSlot}
        navSlotPlacement={navSlotPlacement}
        contentHeaderSlot={contentHeaderSlot}
        mobileNavSlot={mobileNavSlot}
      >
        {children}
      </GlobalNavigationShellContent>
    </NotificationProvider>
  );
};

export default GlobalNavigationShell;
